import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Button, Group, Stack, Tabs } from "@mantine/core";
import { useDisclosure, useReducedMotion } from "@mantine/hooks";
import {
  IconDownload,
  IconAdjustmentsHorizontal,
  IconSettings,
} from "@tabler/icons-react";
import { INSIGHTS_ANALYSIS_SECTIONS } from "@congrs/schemas";

import { AuthContext } from "../services/context";
import { getCurrentWorkspace } from "../services/userConfig";
import { useSetActionButtons } from "../services/ActionButtonsContext";
import { useGetAllFeatureFlags } from "../api/featureFlags";
import {
  useGetInsightsAnalysis,
  usePatchInsightsAnalysis,
} from "../api/insights";
import { printInsightsView } from "../utils/printInsightsView";
import "../components/Insights/insights.print.css";

import EventSummary from "../components/Insights/EventSummary";
import SectionSettingsDrawer from "../components/Insights/SectionSettingsDrawer";
import { SectionPartsContext } from "../components/Insights/SectionShell";
import PrintHeader from "../components/Insights/PrintHeader";
import {
  GROUP_LABELS,
  PART_LABELS,
  SECTION_LABELS,
} from "../components/Insights/sectionMeta";

import AttendeeAccess from "../components/Insights/sections/AttendeeAccess";
import AgendaUsage from "../components/Insights/sections/AgendaUsage";
import ProfileViews from "../components/Insights/sections/ProfileViews";
import PagesUsage from "../components/Insights/sections/PagesUsage";
import Notifications from "../components/Insights/sections/Notifications";
import AppAdoption from "../components/Insights/sections/AppAdoption";
import QrCheckIn from "../components/Insights/sections/QrCheckIn";
import InviteFunnel from "../components/Insights/sections/InviteFunnel";
import Registrations from "../components/Insights/sections/Registrations";
import PollsAnalysis from "../components/Insights/sections/PollsAnalysis";
import SurveysAnalysis from "../components/Insights/sections/SurveysAnalysis";
import Giveaway from "../components/Insights/sections/Giveaway";
import InterestsTags from "../components/Insights/sections/InterestsTags";
import EngagementSegmentation from "../components/Insights/sections/EngagementSegmentation";
import Compliance from "../components/Insights/sections/Compliance";

const SECTION_COMPONENTS = {
  attendeeAccess: AttendeeAccess,
  agendaUsage: AgendaUsage,
  profileViews: ProfileViews,
  pagesUsage: PagesUsage,
  notifications: Notifications,
  appAdoption: AppAdoption,
  qrCheckIn: QrCheckIn,
  inviteFunnel: InviteFunnel,
  registrations: Registrations,
  pollsAnalysis: PollsAnalysis,
  surveysAnalysis: SurveysAnalysis,
  giveaway: Giveaway,
  interestsTags: InterestsTags,
  engagementSegmentation: EngagementSegmentation,
  compliance: Compliance,
};

// Combined height of the fixed TopHeader + AppShell header (see AppShell.jsx).
const APP_HEADER_OFFSET = 100;

// Distance from the viewport top to where section content should land when
// jumped to: fixed app headers + the sticky insights toolbar.
const getScrollOffset = () =>
  APP_HEADER_OFFSET +
  (document.querySelector(".insights-sticky-header")?.offsetHeight ?? 0);

const scrollToSection = (key) => {
  const el = document.getElementById(key);
  if (!el) return;
  const top =
    el.getBoundingClientRect().top + window.scrollY - getScrollOffset() - 8;
  window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
};

// Scrollspy: returns the key of the section currently at the top of the
// viewport (below the fixed headers and sticky toolbar).
function useActiveSection(keys) {
  const [active, setActive] = useState(keys[0] ?? null);

  useEffect(() => {
    if (keys.length === 0) return undefined;
    const onScroll = () => {
      const offset = getScrollOffset() + 16;
      let current = keys[0];
      for (const key of keys) {
        const el = document.getElementById(key);
        if (el && el.getBoundingClientRect().top <= offset) current = key;
      }
      // A short last section may never reach the toolbar offset, so force
      // it active once the page is scrolled to the bottom.
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 2;
      if (atBottom) current = keys[keys.length - 1];
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [keys]);

  return active;
}

function SectionChips({ sections }) {
  const keys = useMemo(() => sections.map((s) => s.key), [sections]);
  const activeKey = useActiveSection(keys);
  const reduceMotion = useReducedMotion();

  // Sliding pill behind the active chip (segmented-control style). Measured
  // from the chip's offset box so it follows wrapping onto multiple rows.
  const containerRef = useRef(null);
  const [indicator, setIndicator] = useState(null);

  useEffect(() => {
    const measure = () => {
      if (!activeKey) return;
      const el = containerRef.current?.querySelector(
        `[data-chip-key="${activeKey}"]`
      );
      if (!el) return;
      setIndicator({
        left: el.offsetLeft,
        top: el.offsetTop,
        width: el.offsetWidth,
        height: el.offsetHeight,
      });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [activeKey, sections]);

  if (sections.length < 2) return null;

  const transition = reduceMotion ? "none" : "220ms ease";
  return (
    <Group gap={6} wrap="wrap" ref={containerRef} pos="relative">
      {indicator && (
        <Box
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            transform: `translate(${indicator.left}px, ${indicator.top}px)`,
            width: indicator.width,
            height: indicator.height,
            borderRadius: 999,
            background: "var(--mantine-primary-color-filled)",
            transition: `transform ${transition}, width ${transition}`,
            pointerEvents: "none",
          }}
        />
      )}
      {sections.map((s) => {
        const isActive = s.key === activeKey;
        return (
          <Button
            key={s.key}
            data-chip-key={s.key}
            size="compact-xs"
            variant="subtle"
            color="gray"
            radius="xl"
            onClick={() => scrollToSection(s.key)}
            style={{
              position: "relative",
              zIndex: 1,
              transition: `color ${transition}`,
              // The subtle variant inherits --button-color from ancestors,
              // so set it explicitly for both states.
              "--button-color": isActive
                ? "var(--mantine-color-white)"
                : "var(--mantine-color-dimmed)",
              "--button-hover": isActive ? "transparent" : undefined,
            }}
          >
            {s.label}
          </Button>
        );
      })}
    </Group>
  );
}

function Insights() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);
  const eventId = currentWorkspace?.eventId;
  const isSysAdmin = user?.role === "sysAdmin";

  const [searchParams, setSearchParams] = useSearchParams();

  const [drawerOpened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false);

  const actions = useMemo(
    () => [
      ...(isSysAdmin
        ? [
            {
              label: "Customize sections",
              icon: <IconSettings />,
              onClick: openDrawer,
            },
            {
              label: "App feature usage weights",
              icon: <IconAdjustmentsHorizontal />,
              onClick: () => navigate("/app/insights/app-usage-weights"),
            },
          ]
        : []),
      {
        label: "Download PDF",
        icon: <IconDownload />,
        onClick: printInsightsView,
      },
    ],
    [isSysAdmin, navigate, openDrawer]
  );

  useSetActionButtons(actions);

  const { data: analysis } = useGetInsightsAnalysis(eventId);
  const { mutate: patchAnalysis, isPending: isPatchingEnabled } =
    usePatchInsightsAnalysis(eventId);

  const { data: flags } = useGetAllFeatureFlags({ eventId });
  const enabledFlagSet = useMemo(
    () =>
      new Set(
        (Array.isArray(flags) ? flags : [])
          .filter((f) => f.enabled)
          .map((f) => f.featureName)
      ),
    [flags]
  );

  const availableSections = useMemo(
    () =>
      INSIGHTS_ANALYSIS_SECTIONS.filter(
        (s) => !s.featureFlag || enabledFlagSet.has(s.featureFlag)
      ),
    [enabledFlagSet]
  );

  const enabledKeys = useMemo(() => {
    const set = new Set();
    for (const s of availableSections) {
      if (analysis?.sections?.[s.key]?.isEnabled !== false) set.add(s.key);
    }
    return set;
  }, [availableSections, analysis]);

  const queryClient = useQueryClient();
  const analysisQueryKey = ["insights", "analysis", eventId];

  const patchSection = (key, patch) => {
    queryClient.cancelQueries({ queryKey: analysisQueryKey });
    queryClient.setQueryData(analysisQueryKey, (prev) => {
      if (!prev) return prev;
      const section = prev.sections?.[key] ?? {};
      return {
        ...prev,
        sections: {
          ...prev.sections,
          [key]: {
            ...section,
            ...patch,
            parts: { ...section.parts, ...patch.parts },
          },
        },
      };
    });
    patchAnalysis(
      { sections: { [key]: patch } },
      {
        onError: () =>
          queryClient.invalidateQueries({ queryKey: analysisQueryKey }),
      }
    );
  };

  const toggle = (key, on) => patchSection(key, { isEnabled: on });

  const isPartShown = (key, part) =>
    analysis?.sections?.[key]?.parts?.[part] !== false;

  const togglePart = (key, part, on) =>
    patchSection(key, { parts: { [part]: on } });

  // All available sections per group (for the settings drawer).
  const groupedSections = useMemo(() => {
    const out = {};
    for (const s of availableSections) {
      const item = {
        key: s.key,
        label: SECTION_LABELS[s.key] ?? s.key,
        parts: s.parts.map((part) => ({
          key: part,
          label: PART_LABELS[s.key]?.[part] ?? part,
        })),
      };
      (out[s.group] ||= []).push(item);
    }
    return out;
  }, [availableSections]);

  // Enabled, component-backed sections per group (for tabs and rendering).
  const visibleGroups = useMemo(() => {
    const out = [];
    for (const s of availableSections) {
      const parts = analysis?.sections?.[s.key]?.parts;
      const allPartsHidden =
        s.parts.length > 0 && s.parts.every((part) => parts?.[part] === false);
      if (
        !enabledKeys.has(s.key) ||
        !SECTION_COMPONENTS[s.key] ||
        allPartsHidden
      )
        continue;
      const item = { key: s.key, label: SECTION_LABELS[s.key] ?? s.key };
      const group = out.find((g) => g.group === s.group);
      if (group) group.sections.push(item);
      else out.push({ group: s.group, sections: [item] });
    }
    return out;
  }, [availableSections, enabledKeys, analysis]);

  const tabParam = searchParams.get("tab");
  const activeTab = visibleGroups.some((g) => g.group === tabParam)
    ? tabParam
    : visibleGroups[0]?.group;

  const activeGroup = visibleGroups.find((g) => g.group === activeTab);

  const handleTabChange = (next) => {
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev);
      out.set("tab", next);
      return out;
    });
    window.scrollTo({ top: 0 });
  };

  return (
    <Stack gap="md" className="insights-print-root">
      <PrintHeader eventId={eventId} />

      {/* Sticky toolbar: tabs + quick-jump chips. Negative
          horizontal margins extend the background over AppShell.Main's
          padding gutters so content doesn't peek through while scrolling. */}
      <Box
        className="insights-print-hide insights-sticky-header"
        style={{
          position: "sticky",
          top: APP_HEADER_OFFSET,
          zIndex: 10,
          background: "var(--mantine-color-body)",
          marginInline: "calc(var(--mantine-spacing-md) * -1)",
          paddingInline: "var(--mantine-spacing-md)",
          paddingBlock: "var(--mantine-spacing-xs)",
          borderBottom: "1px solid var(--mantine-color-default-border)",
        }}
      >
        <Stack gap="xs">
          <Group
            justify="space-between"
            align="flex-start"
            wrap="wrap"
            gap="sm"
          >
            {visibleGroups.length > 1 ? (
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="pills"
                radius="xl"
              >
                <Tabs.List>
                  {visibleGroups.map((g) => (
                    <Tabs.Tab key={g.group} value={g.group}>
                      {GROUP_LABELS[g.group] ?? g.group}
                    </Tabs.Tab>
                  ))}
                </Tabs.List>
              </Tabs>
            ) : (
              <span />
            )}
          </Group>
          {activeGroup && (
            <SectionChips
              key={activeGroup.group}
              sections={activeGroup.sections}
            />
          )}
        </Stack>
      </Box>

      <EventSummary eventId={eventId} />

      {/* Inactive groups stay mounted at full width (height 0, clipped) so
          their charts keep real dimensions and the PDF export can print
          every section, not just the active tab. */}
      {visibleGroups.map((g) => {
        const isActive = g.group === activeTab;
        return (
          <div
            key={g.group}
            className="insights-group"
            style={
              isActive
                ? undefined
                : {
                    height: 0,
                    overflow: "hidden",
                    // Cancel the parent Stack's gap so collapsed groups
                    // don't add phantom spacing between visible siblings.
                    marginTop: "calc(var(--mantine-spacing-md) * -1)",
                  }
            }
          >
            <Stack gap="lg">
              {g.sections.map((s) => {
                const Component = SECTION_COMPONENTS[s.key];
                return (
                  <SectionPartsContext.Provider
                    key={s.key}
                    value={analysis?.sections?.[s.key]?.parts}
                  >
                    <Component eventId={eventId} />
                  </SectionPartsContext.Provider>
                );
              })}
            </Stack>
          </div>
        );
      })}

      {isSysAdmin && (
        <SectionSettingsDrawer
          opened={drawerOpened}
          onClose={closeDrawer}
          groupedSections={groupedSections}
          enabledKeys={enabledKeys}
          onToggle={toggle}
          isPartShown={isPartShown}
          onTogglePart={togglePart}
          isAnalysisEnabled={Boolean(analysis?.isEnabled)}
          isPatchingEnabled={isPatchingEnabled}
          onToggleAnalysisEnabled={(value) =>
            patchAnalysis({ isEnabled: value })
          }
        />
      )}
    </Stack>
  );
}

export default Insights;
